import 'antd/dist/antd.css';
import '~/assets/splitters.css';
import '~/assets/style.css';
import '~/components/App.css';

import {
    captureException,
    ErrorBoundary as SentryErrorBoundary,
} from '@sentry/react';
import { StoreProvider } from 'easy-peasy';
import { useEffect } from 'react';
import {
    Links,
    Meta,
    Outlet,
    Scripts,
    ScrollRestoration,
    useRouteError,
} from 'react-router';
import { Provider as UrqlProvider } from 'urql';

import { LanguageWrapper } from '~/components/shared/Layout/LanguageWrapper';
import { pageTitle } from '~/lib/meta';
import { urqlClient } from '~/lib/urql';
import { store } from '~/store/store';

/**
 * Default document title. Overridden per-route by route-module `meta` exports.
 */
export function meta() {
    return [{ title: pageTitle() }];
}

export function Layout({ children }: { children: React.ReactNode }) {
    return (
        <html lang="de">
            <head>
                <meta charSet="UTF-8" />
                <meta httpEquiv="X-UA-Compatible" content="IE=edge" />
                <meta
                    name="viewport"
                    content="width=device-width, initial-scale=1.0"
                />
                <meta name="robots" content="noindex, follow" />
                <Meta />
                <Links />
            </head>
            <body>
                {children}
                <ScrollRestoration />
                <Scripts />
            </body>
        </html>
    );
}

/**
 * Provider stack wrapping the matched route tree, rendered through `<Outlet>`.
 */
export default function App() {
    return (
        <SentryErrorBoundary fallback={<ErrorFallback />}>
            <UrqlProvider value={urqlClient}>
                <StoreProvider store={store}>
                    <LanguageWrapper>
                        <Outlet />
                    </LanguageWrapper>
                </StoreProvider>
            </UrqlProvider>
        </SentryErrorBoundary>
    );
}

/**
 * Root-level React Router error boundary. Last resort error catcher.
 */
export function ErrorBoundary() {
    const error = useRouteError();

    useEffect(() => {
        captureException(error);
    }, [error]);

    return <ErrorFallback />;
}

/**
 * Static, multilingual error text for the boundaries below. Last resort
 * error output, hardly ever shown to users.
 *
 * This is used by the root level error boundary and Sentry's error boundary, both.
 *
 * Note: This is outside the whole application tree. We can't use the translation
 * library here.
 */
function ErrorFallback() {
    return (
        <>
            <p lang="de">
                Es ist ein Fehler aufgetreten. Melden Sie sich bei einer
                Lehrperson oder Administrator:in.
            </p>
            <p lang="fr">
                Une erreur s’est produite. Contactez un enseignant ou un
                administrateur.
            </p>
            <p lang="it">
                Si è verificato un errore. Contatta l’insegnante o un
                amministratore.
            </p>
            <p lang="en">
                An error has occurred. Please contact a tutor or administrator.
            </p>
        </>
    );
}
